'use server'

import { Partner, CreatePartnerData, UpdatePartnerData } from '../common/interfaces/partner.interface'
import { apiRequest, ApiResponse } from '../request'

export async function getPartners(
    params?: {
        companyId?: number
        page?: number
        limit?: number
        search?: string
    },
): Promise<ApiResponse<Partner[]>> {
    return apiRequest<Partner[]>('partners', { params: params ?? {} })
}

export async function getPartnersByCompany(companyId: number): Promise<ApiResponse<Partner[]>> {
    return apiRequest<Partner[]>(`partners/company/${companyId}`, {})
}

/**
 * Parceiros de todas as empresas acessíveis ao usuário. Para telas em escopo de organização
 * (ex.: relatórios com "Todas as filiais"): parceiro é company-scoped, e buscar só na
 * unidade ativa esconderia parceiros de outras filiais que aparecem nos dados exibidos.
 */
export async function searchAccessiblePartners(
    searchTerm?: string,
): Promise<ApiResponse<Partner[]>> {
    const params = searchTerm ? { q: searchTerm } : {}
    return apiRequest<Partner[]>('partners/accessible/search', { params })
}

export async function searchPartners(
    companyId: number,
    searchTerm?: string,
): Promise<ApiResponse<Partner[]>> {
    const params = searchTerm ? { q: searchTerm } : {}
    return apiRequest<Partner[]>(`partners/company/${companyId}/search`, {
        params,
    })
}

export async function getPartner(id: number): Promise<ApiResponse<Partner>> {
    return apiRequest<Partner>(`partners/${id}`, {})
}

export async function createPartner(data: CreatePartnerData): Promise<ApiResponse<Partner>> {
    return apiRequest<Partner>('partners', { data }, 'post')
}

export async function updatePartner(id: number, data: UpdatePartnerData): Promise<ApiResponse<Partner>> {
    return apiRequest<Partner>(`partners/${id}`, { data }, 'patch')
}

export async function deletePartner(id: number): Promise<ApiResponse<void>> {
    return apiRequest<void>(`partners/${id}`, {}, 'delete')
}

export async function togglePartnerActive(id: number): Promise<ApiResponse<Partner>> {
    return apiRequest<Partner>(`partners/${id}/toggle-active`, {}, 'patch')
}

export interface PartnerTransferTarget {
    id: number
    name: string
    tradingName: string | null
}

export interface PartnerTransferResult {
    partner: Partner
    sourceCompanyId: number
    targetCompanyId: number
    moved: Record<string, number>
    totalMoved: number
}

export async function getPartnerTransferTargets(
    id: number,
): Promise<ApiResponse<PartnerTransferTarget[]>> {
    return apiRequest<PartnerTransferTarget[]>(`partners/${id}/transfer-targets`, {})
}

export async function transferPartnerCompany(
    id: number,
    targetCompanyId: number,
): Promise<ApiResponse<PartnerTransferResult>> {
    return apiRequest<PartnerTransferResult>(
        `partners/${id}/transfer-company`,
        { data: { targetCompanyId } },
        'patch',
    )
}

export interface PartnerPricingInfo {
    currentPartners: number
    freePartners: number | 'unlimited'
    pricePerExtra: number
    currentCustomPrice: number
    newCustomPrice: number
    willIncrease: boolean
    increaseAmount: number
    hasResaleFeature: boolean
}

export interface PartnerBilling {
    id: number
    companyId: number
    partnerId: number
    clientId: number | null
    amount: number
    status: string
    dueDate: string
    paidAt: string | null
    paymentMethod: string
    pixCopyPaste: string | null
    pixQrCodeBase64: string | null
    paymentLink: string | null
    description: string | null
    publicToken: string | null
    company?: {
        id: number
        name: string
        tradingName: string | null
    }
}

export interface MyPartnerBillingsResponse {
    partner: {
        id: number
        subscriptionPrice: number | null
        subscriptionRecurrence: string | null
        teleconsultationMinutes: number | null
        monthlySignDocuments: number | null
        monthlyFiscalNotes: number | null
    }
    billings: PartnerBilling[]
}

export async function getPartnerPricingInfo(): Promise<
    ApiResponse<PartnerPricingInfo>
> {
    return apiRequest<PartnerPricingInfo>('partners/pricing-info', {})
}

export async function getMyPartnerBillings(): Promise<
    ApiResponse<MyPartnerBillingsResponse>
> {
    return apiRequest<MyPartnerBillingsResponse>('partners/me/billings', {})
}

export interface ResourceLimitInfo {
    planLimit: number | 'unlimited'
    allocated: number
    available: number | 'unlimited'
}

export interface PartnerResourceLimits {
    teleconsultationMinutes: ResourceLimitInfo
    monthlySignDocuments: ResourceLimitInfo
    monthlyFiscalNotes: ResourceLimitInfo
}

export async function getPartnerResourceLimits(
    companyId: number,
    excludePartnerId?: number,
): Promise<ApiResponse<PartnerResourceLimits>> {
    const params: any = {}
    if (excludePartnerId) params.excludePartnerId = excludePartnerId
    return apiRequest<PartnerResourceLimits>(
        `subscriptions/partner-resource-limits/${companyId}`,
        { params },
    )
}

export async function sendPartnerAccessCode(id: number): Promise<ApiResponse<{ message: string }>> {
    return apiRequest<{ message: string }>(`partners/${id}/send-access-code`, {}, 'post')
}

export async function regeneratePartnerAccessCode(id: number): Promise<ApiResponse<{ id: number; accessCode: string }>> {
    return apiRequest<{ id: number; accessCode: string }>(`partners/${id}/regenerate-access-code`, {}, 'post')
}

export async function uploadPartnerLogo(
    partnerId: number,
    file: File
): Promise<ApiResponse<{ id: number; name: string; tradingName: string | null; logoUrl: string }>> {
    const formData = new FormData()
    formData.append('logo', file)
    return apiRequest<{ id: number; name: string; tradingName: string | null; logoUrl: string }>(
        `partners/${partnerId}/logo`,
        { data: formData, formData: true },
        'post'
    )
}
